import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { useAnalytics } from "@/hooks/useAnalytics";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import { DynamicSitemap } from "@/components/DynamicSitemap";
import { ScrollProgress } from "@/components/ScrollProgress";
import ScrollToTop from "@/components/ScrollToTop";
import { lazyWithRetry } from "@/lib/lazyWithRetry";

// The homepage stays in the main bundle so the first paint never waits on an
// extra request. Every other page loads its own code on demand.
import Index from "./pages/Index";

const Calculators = lazyWithRetry(() => import("./pages/Calculators"));
const PositionSizeCalculator = lazyWithRetry(() => import("./pages/PositionSizeCalculator"));
const RiskRewardCalculator = lazyWithRetry(() => import("./pages/RiskRewardCalculator"));
const ProfitLossCalculator = lazyWithRetry(() => import("./pages/ProfitLossCalculator"));
const CompoundInterestCalculator = lazyWithRetry(() => import("./pages/CompoundInterestCalculator"));
const FibonacciCalculator = lazyWithRetry(() => import("./pages/FibonacciCalculator"));
const FuturesMarginCalculator = lazyWithRetry(() => import("./pages/FuturesMarginCalculator"));
const TickValueCalculator = lazyWithRetry(() => import("./pages/TickValueCalculator"));
const ATRStopCalculator = lazyWithRetry(() => import("./pages/ATRStopCalculator"));
const DrawdownCalculator = lazyWithRetry(() => import("./pages/DrawdownCalculator"));
const ProfitTargetCalculator = lazyWithRetry(() => import("./pages/ProfitTargetCalculator"));
const TradingJournal = lazyWithRetry(() => import("./pages/TradingJournal"));
const ContactUs = lazyWithRetry(() => import("./pages/ContactUs"));
const TradingFAQ = lazyWithRetry(() => import("./pages/TradingFAQ"));
const RiskDisclosure = lazyWithRetry(() => import("./pages/RiskDisclosure"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithRetry(() => import("./pages/TermsOfService"));
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy"));
const Disclaimer = lazyWithRetry(() => import("./pages/Disclaimer"));
const AffiliateDisclosure = lazyWithRetry(() => import("./pages/AffiliateDisclosure"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const BlogCategory = lazyWithRetry(() => import("./pages/BlogCategory"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const Screener = lazyWithRetry(() => import("./pages/Screener"));
const TradingPsychology = lazyWithRetry(() => import("./pages/TradingPsychology"));
const FuturesTradingGuide = lazyWithRetry(() => import("./pages/FuturesTradingGuide"));
const DayTradingVsSwingTrading = lazyWithRetry(() => import("./pages/DayTradingVsSwingTrading"));
const MarginRequirements = lazyWithRetry(() => import("./pages/MarginRequirements"));
const CommodityTradingBasics = lazyWithRetry(() => import("./pages/CommodityTradingBasics"));
const OptionsVsFutures = lazyWithRetry(() => import("./pages/OptionsVsFutures"));
const EducationalResources = lazyWithRetry(() => import("./pages/EducationalResources"));
const DayTradingStrategies = lazyWithRetry(() => import("./pages/DayTradingStrategies"));
const SwingTradingStrategies = lazyWithRetry(() => import("./pages/SwingTradingStrategies"));
const PropFirmComparison = lazyWithRetry(() => import("./pages/PropFirmComparison"));
const ApexTraderFundingReview = lazyWithRetry(() => import("./pages/ApexTraderFundingReview"));
const ApexConsistencyRule = lazyWithRetry(() => import("./pages/ApexConsistencyRule"));
const ApexTrailingDrawdownCalculator = lazyWithRetry(() => import("./pages/ApexTrailingDrawdownCalculator"));
const ApexPayoutProof = lazyWithRetry(() => import("./pages/ApexPayoutProof"));
const ApexAlternatives = lazyWithRetry(() => import("./pages/ApexAlternatives"));
const HowHardIsApex = lazyWithRetry(() => import("./pages/HowHardIsApex"));
const ApexRules = lazyWithRetry(() => import("./pages/ApexRules"));
const ApexCouponCode = lazyWithRetry(() => import("./pages/ApexCouponCode"));
const ApexLegit = lazyWithRetry(() => import("./pages/ApexLegit"));
const ApexEvaluation = lazyWithRetry(() => import("./pages/ApexEvaluation"));
const ApexPlatforms = lazyWithRetry(() => import("./pages/ApexPlatforms"));
const ApexArticle = lazyWithRetry(() => import("./pages/ApexArticle"));
const ApexLoginHub = lazyWithRetry(() => import("./pages/ApexLoginHub"));
const Apex25kAccount = lazyWithRetry(() => import("./pages/Apex25kAccount"));
const Apex50kAccount = lazyWithRetry(() => import("./pages/Apex50kAccount"));
const Apex75kAccount = lazyWithRetry(() => import("./pages/Apex75kAccount"));
const Apex100kAccount = lazyWithRetry(() => import("./pages/Apex100kAccount"));
const Apex150kAccount = lazyWithRetry(() => import("./pages/Apex150kAccount"));
const Apex250kAccount = lazyWithRetry(() => import("./pages/Apex250kAccount"));
const Apex300kAccount = lazyWithRetry(() => import("./pages/Apex300kAccount"));
const PropFirmVersus = lazyWithRetry(() => import("./pages/PropFirmVersus"));
const PillarPage = lazyWithRetry(() => import("./pages/PillarPage"));
const AboutUs = lazyWithRetry(() => import("./pages/AboutUs"));
const EditorialPolicy = lazyWithRetry(() => import("./pages/EditorialPolicy"));
const PropFirmTestingMethodology = lazyWithRetry(() => import("./pages/PropFirmTestingMethodology"));
const FreeTradingSecrets = lazyWithRetry(() => import("./pages/FreeTradingSecrets"));
const FundedTraderGuide = lazyWithRetry(() => import("./pages/FundedTraderGuide"));
const EducationGuidePage = lazyWithRetry(() => import("./pages/EducationGuidePage"));
const ApexCalculators = lazyWithRetry(() => import("./pages/ApexCalculators"));
const PropFirmAccountSizeCalculator = lazyWithRetry(() => import("./pages/PropFirmAccountSizeCalculator"));
const PropFirmCostCalculator = lazyWithRetry(() => import("./pages/PropFirmCostCalculator"));
const PropFirmPayoutCalculator = lazyWithRetry(() => import("./pages/PropFirmPayoutCalculator"));
const PropFirmRuleUpdates = lazyWithRetry(() => import("./pages/PropFirmRuleUpdates"));
const ApexResourceHub = lazyWithRetry(() => import("./pages/ApexResourceHub"));
const BestFuturesPropFirms = lazyWithRetry(() => import("./pages/BestFuturesPropFirms"));
const CheapestPropFirms = lazyWithRetry(() => import("./pages/CheapestPropFirms"));
const FastestPayoutPropFirms = lazyWithRetry(() => import("./pages/FastestPayoutPropFirms"));
const FuturesGlossary = lazyWithRetry(() => import("./pages/FuturesGlossary"));
const HowToGuidesIndex = lazyWithRetry(() => import("./pages/HowToGuidesIndex"));
const HowToPassApexEvaluation = lazyWithRetry(() => import("./pages/HowToPassApexEvaluation"));
const HowToWithdrawApex = lazyWithRetry(() => import("./pages/HowToWithdrawApex"));
const HowToAvoidTrailingDrawdown = lazyWithRetry(() => import("./pages/HowToAvoidTrailingDrawdown"));
const HowToScaleApex = lazyWithRetry(() => import("./pages/HowToScaleApex"));
const MicroFuturesIndex = lazyWithRetry(() => import("./pages/MicroFuturesIndex"));
const MicroFutureGuidePage = lazyWithRetry(() => import("./pages/MicroFutureGuidePage"));
const TradingPlatformsIndex = lazyWithRetry(() => import("./pages/TradingPlatformsIndex"));
const PlatformGuidePage = lazyWithRetry(() => import("./pages/PlatformGuidePage"));
const TradingStrategiesIndex = lazyWithRetry(() => import("./pages/TradingStrategiesIndex"));
const StrategyPlaybookPage = lazyWithRetry(() => import("./pages/StrategyPlaybookPage"));
const TraderPsychologyIndex = lazyWithRetry(() => import("./pages/TraderPsychologyIndex"));
const TraderPsychologyPlaybook = lazyWithRetry(() => import("./pages/TraderPsychologyPlaybook"));
const NewsTradingIndex = lazyWithRetry(() => import("./pages/NewsTradingIndex"));
const NewsEventPlaybook = lazyWithRetry(() => import("./pages/NewsEventPlaybook"));
const TradingSessionsIndex = lazyWithRetry(() => import("./pages/TradingSessionsIndex"));
const TradingSessionPlaybook = lazyWithRetry(() => import("./pages/TradingSessionPlaybook"));
const RiskManagementIndex = lazyWithRetry(() => import("./pages/RiskManagementIndex"));
const RiskPlaybookPage = lazyWithRetry(() => import("./pages/RiskPlaybookPage"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const AdminPage = lazyWithRetry(() => import("@/components/AdminPage"));
import AdminProtection from "@/components/AdminProtection";

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-live="polite">
    <div className="h-8 w-8 rounded-full border-2 border-muted border-t-trading-primary animate-spin" />
    <span className="sr-only">Loading page</span>
  </div>
);

const queryClient = new QueryClient();

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  useAnalytics();
  
  const getActiveTab = () => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    
    if (path === '/' && searchParams.get('tab')) {
      return searchParams.get('tab') || 'home';
    }
    
    switch (path) {
      case '/':
        return 'home';
      case '/calculators':
        return 'calculators';
      case '/trading-journal':
        return 'journal';
      case '/screener':
        return 'screener';
      case '/psychology':
        return 'psychology';
      case '/educational-resources':
        return 'education';
      default:
        return '';
    }
  };
  
  const handleTabChange = (tab: string) => {
    if (location.pathname === '/') {
      window.dispatchEvent(new CustomEvent('tabChange', { detail: tab }));
    } else {
      if (tab === "home") {
        navigate("/");
      } else if (tab === "calculators") {
        navigate("/calculators");
      } else if (tab === "journal") {
        navigate("/trading-journal");
      } else if (tab === "screener") {
        navigate("/screener");
      } else if (tab === "psychology") {
        navigate("/psychology");
      } else if (tab === "education") {
        navigate("/educational-resources");
      } else {
        navigate(`/?tab=${tab}`);
      }
    }
  };

  const handleSearch = (query: string) => {
    navigate(`/blog?search=${encodeURIComponent(query)}`);
  };

  // Standalone landing pages render without header/footer
  const isStandalone = location.pathname === "/free-trading-secrets";

  if (isStandalone) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/free-trading-secrets" element={<FreeTradingSecrets />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <DynamicSitemap />
      <ScrollProgress />
      <Header activeTab={getActiveTab()} onTabChange={handleTabChange} onSearch={handleSearch} />
      <main className="flex-1">
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/sitemap.xml" element={<DynamicSitemap />} />
          <Route path="/screener" element={<Screener />} />
          <Route path="/psychology" element={<TradingPsychology />} />
          <Route path="/calculators" element={<Calculators />} />
          <Route path="/calculators/position-size" element={<PositionSizeCalculator />} />
          <Route path="/calculators/risk-reward" element={<RiskRewardCalculator />} />
          <Route path="/calculators/profit-loss" element={<ProfitLossCalculator />} />
          <Route path="/calculators/compound-interest" element={<CompoundInterestCalculator />} />
          <Route path="/calculators/fibonacci" element={<FibonacciCalculator />} />
          <Route path="/calculators/futures-margin" element={<FuturesMarginCalculator />} />
          <Route path="/calculators/tick-value" element={<TickValueCalculator />} />
          <Route path="/calculators/atr-stop" element={<ATRStopCalculator />} />
          <Route path="/calculators/drawdown" element={<DrawdownCalculator />} />
          <Route path="/calculators/profit-target" element={<ProfitTargetCalculator />} />
          <Route path="/trading-journal" element={<TradingJournal />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/trading-faq" element={<TradingFAQ />} />
          <Route path="/risk-disclosure" element={<RiskDisclosure />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/category/:category" element={<BlogCategory />} />
          <Route path="/blog/:id" element={<BlogPost />} />
          <Route path="/futures-trading-guide" element={<FuturesTradingGuide />} />
          <Route path="/day-trading-vs-swing-trading" element={<DayTradingVsSwingTrading />} />
          <Route path="/margin-requirements" element={<MarginRequirements />} />
          <Route path="/commodity-trading-basics" element={<CommodityTradingBasics />} />
          <Route path="/options-vs-futures" element={<OptionsVsFutures />} />
          <Route path="/educational-resources" element={<EducationalResources />} />
          <Route path="/day-trading-strategies" element={<DayTradingStrategies />} />
          <Route path="/swing-trading-strategies" element={<SwingTradingStrategies />} />
          <Route path="/prop-firms" element={<PropFirmComparison />} />
          <Route path="/prop-firms/:matchup" element={<PropFirmVersus />} />
          <Route path="/hub/:pillarSlug" element={<PillarPage />} />
          <Route path="/apex-trader-funding-review" element={<ApexTraderFundingReview />} />
          <Route path="/apex-consistency-rule-explained" element={<ApexConsistencyRule />} />
          <Route path="/apex-trailing-drawdown-calculator" element={<ApexTrailingDrawdownCalculator />} />
          <Route path="/apex-payout-proof" element={<ApexPayoutProof />} />
          <Route path="/apex-alternatives" element={<ApexAlternatives />} />
          <Route path="/how-hard-is-apex-trader-funding" element={<HowHardIsApex />} />
          <Route path="/apex-trader-funding-rules" element={<ApexRules />} />
          <Route path="/apex-trader-funding-coupon-code" element={<ApexCouponCode />} />
          <Route path="/is-apex-trader-funding-legit" element={<ApexLegit />} />
          <Route path="/apex-trader-funding-evaluation" element={<ApexEvaluation />} />
          <Route path="/apex-trader-funding-platforms" element={<ApexPlatforms />} />
          <Route path="/apex-trader-funding-login" element={<ApexLoginHub />} />
          <Route path="/apex/:slug" element={<ApexArticle />} />
          <Route path="/apex-25k-account" element={<Apex25kAccount />} />
          <Route path="/apex-50k-account" element={<Apex50kAccount />} />
          <Route path="/apex-75k-account" element={<Apex75kAccount />} />
          <Route path="/apex-100k-account" element={<Apex100kAccount />} />
          <Route path="/apex-150k-account" element={<Apex150kAccount />} />
          <Route path="/apex-250k-account" element={<Apex250kAccount />} />
          <Route path="/apex-300k-account" element={<Apex300kAccount />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/editorial-policy" element={<EditorialPolicy />} />
          <Route path="/prop-firm-testing-methodology" element={<PropFirmTestingMethodology />} />
          <Route path="/funded-trader-guide" element={<FundedTraderGuide />} />
          <Route path="/how-to-pass-prop-firm-evaluation" element={<EducationGuidePage />} />
          <Route path="/funded-account-risk-management" element={<EducationGuidePage />} />
          <Route path="/prop-firm-payout-guide" element={<EducationGuidePage />} />
          <Route path="/futures-trading-psychology-guide" element={<EducationGuidePage />} />
          <Route path="/apex-calculators" element={<ApexCalculators />} />
          <Route path="/prop-firm-account-size-calculator" element={<PropFirmAccountSizeCalculator />} />
          <Route path="/prop-firm-cost-calculator" element={<PropFirmCostCalculator />} />
          <Route path="/prop-firm-payout-calculator" element={<PropFirmPayoutCalculator />} />
          <Route path="/prop-firm-rule-updates" element={<PropFirmRuleUpdates />} />
          <Route path="/apex-trader-funding-hub" element={<ApexResourceHub />} />
          <Route path="/best-futures-prop-firms" element={<BestFuturesPropFirms />} />
          <Route path="/cheapest-futures-prop-firms" element={<CheapestPropFirms />} />
          <Route path="/fastest-payout-prop-firms" element={<FastestPayoutPropFirms />} />
          <Route path="/futures-trading-glossary" element={<FuturesGlossary />} />
          <Route path="/how-to-guides" element={<HowToGuidesIndex />} />
          <Route path="/how-to-pass-apex-trader-funding-evaluation" element={<HowToPassApexEvaluation />} />
          <Route path="/how-to-withdraw-from-apex-trader-funding" element={<HowToWithdrawApex />} />
          <Route path="/how-to-avoid-apex-trailing-drawdown" element={<HowToAvoidTrailingDrawdown />} />
          <Route path="/how-to-scale-apex-trader-funding-accounts" element={<HowToScaleApex />} />
          <Route path="/micro-futures" element={<MicroFuturesIndex />} />
          <Route path="/micro-futures/:slug" element={<MicroFutureGuidePage />} />
          <Route path="/trading-platforms" element={<TradingPlatformsIndex />} />
          <Route path="/trading-platforms/:slug" element={<PlatformGuidePage />} />
          <Route path="/trading-strategies" element={<TradingStrategiesIndex />} />
          <Route path="/trading-strategies/:slug" element={<StrategyPlaybookPage />} />
          <Route path="/trader-psychology" element={<TraderPsychologyIndex />} />
          <Route path="/trader-psychology/:slug" element={<TraderPsychologyPlaybook />} />
          <Route path="/news-trading" element={<NewsTradingIndex />} />
          <Route path="/news-trading/:slug" element={<NewsEventPlaybook />} />
          <Route path="/trading-sessions" element={<TradingSessionsIndex />} />
          <Route path="/trading-sessions/:slug" element={<TradingSessionPlaybook />} />
          <Route path="/risk-management" element={<RiskManagementIndex />} />
          <Route path="/risk-management/:slug" element={<RiskPlaybookPage />} />
          <Route path="/admin" element={
            <AdminProtection>
              <AdminPage />
            </AdminProtection>
          } />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <Footer onTabChange={handleTabChange} />
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AppContent />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
